import React, { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { Header, Icon, Label, Message } from 'semantic-ui-react'
import moment from 'moment'
import LoadingSpinner from '../../LoadingSpinner'
import { LOAD_EXTERNAL_EXPERT_QUERY } from '../../../graphql/admin'
import {
    KIND_LABELS,
    MATCH_STATUS_LABELS,
    SEARCH_LABELS,
    byMatchStatus,
    displayName,
    factName,
    matchExplanation,
    registrationOf,
    splitEngineReason,
    splitOnTerms,
    termsToHighlight,
} from './helpers'
import '../styles.css'
import './styles.css'

const Highlighted = ({ text, terms }) => (
    <>
        {splitOnTerms(text, terms).map((part, index) =>
            part.match ? (
                // eslint-disable-next-line react/no-array-index-key
                <mark key={index} className="sourcing-mark">
                    {part.text}
                </mark>
            ) : (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={index}>{part.text}</React.Fragment>
            )
        )}
    </>
)

const DetailItem = ({ label, children }) => (
    <div className="auth-user-detail-item">
        <div className="auth-user-detail-label">{label}</div>
        <div>{children || '—'}</div>
    </div>
)

const PageCard = ({ page, terms }) => {
    const matchStatus =
        MATCH_STATUS_LABELS[page.matchStatus] || MATCH_STATUS_LABELS.not_checked

    return (
        <div className="sourcing-page">
            <div className="sourcing-page-head">
                <a href={page.url} target="_blank" rel="noopener noreferrer">
                    {page.pageTitle || page.url}
                </a>
                <Label
                    size="tiny"
                    color={matchStatus.color}
                    basic={matchStatus.basic}
                >
                    {matchStatus.text}
                </Label>
            </div>

            <div className="sourcing-page-meta">
                <span className="sourcing-host">{page.host}</span>
                <Label size="tiny" basic>
                    Kind of site: {KIND_LABELS[page.kind] || page.kind}
                </Label>
                <Label size="tiny" basic>
                    Found by: {SEARCH_LABELS[page.foundBy] || page.foundBy}
                </Label>
                {(page.echoed || []).length > 0 && (
                    <Label size="tiny" color="teal" basic>
                        Page repeats: {page.echoed.map(factName).join(', ')}
                    </Label>
                )}
            </div>

            {/* Why the model decided what it did. Identity is the only
                judgement this pipeline makes and it has no automated check,
                so this line is what makes a match arguable. */}
            {matchExplanation(page) && (
                <p className="sourcing-reason">
                    <span className="sourcing-reason-label">
                        {page.matchStatus === 'not_checked'
                            ? 'Why no decision'
                            : 'Why the check decided this'}
                    </span>
                    {matchExplanation(page)}
                </p>
            )}

            {(page.links || []).length > 0 && (
                <p className="sourcing-links">
                    <span className="sourcing-reason-label">
                        Contact details and profile links on this page
                    </span>
                    {page.links.map((link) => (
                        <span key={link} className="sourcing-link">
                            {link}
                        </span>
                    ))}
                </p>
            )}

            {page.snippet && (
                <p className="sourcing-text">
                    <Highlighted text={page.snippet} terms={terms} />
                </p>
            )}
            {page.pageText && (
                <p className="sourcing-text sourcing-pagetext">
                    <Highlighted text={page.pageText} terms={terms} />
                </p>
            )}
        </div>
    )
}

const ExternalExpertDetail = () => {
    const { registrationNumber } = useParams()
    const [showOtherPeople, setShowOtherPeople] = useState(false)
    const { data, loading, error } = useQuery(LOAD_EXTERNAL_EXPERT_QUERY, {
        variables: { registrationNumber },
        fetchPolicy: 'cache-and-network',
    })

    if (loading && !data) return <LoadingSpinner />
    if (error) return <Message negative content={error.message} />

    const expert = data?.adminPortal?.externalExpert

    if (!expert) {
        return (
            <div className="auth-users-page">
                <Message
                    warning
                    header="External expert not found"
                    content={`No external expert has the NPI ${registrationNumber}.`}
                />
                <Link to="/admin/sourcing" className="auth-user-back-link">
                    <Icon name="arrow left" /> Back to sourcing
                </Link>
            </div>
        )
    }

    const registration = registrationOf(expert)
    const terms = termsToHighlight(expert)
    const pages = expert.webSources || []
    const possiblyThem = pages
        .filter((page) => page.matchStatus !== 'different_person')
        .sort(byMatchStatus)
    const otherPeople = pages.filter(
        (page) => page.matchStatus === 'different_person'
    )

    return (
        <div className="auth-users-page">
            <Link to="/admin/sourcing" className="auth-user-back-link">
                <Icon name="arrow left" /> Back to sourcing
            </Link>

            <Header as="h2">{displayName(expert)}</Header>
            <p className="auth-user-muted">
                {registration.type} {registration.number} · last run{' '}
                {expert.lastAttemptedAt
                    ? moment(Number(expert.lastAttemptedAt)).format(
                          'D MMM YYYY, HH:mm'
                      )
                    : 'never'}
                {expert.judgedBy?.model
                    ? ` · judged by ${expert.judgedBy.model}`
                    : ''}
            </p>

            <div className="sourcing-layout">
                {/* The fixed point. Every page on the right is judged against
                    these facts, so they stay in view while the list scrolls. */}
                <aside className="sourcing-registry">
                    <Header as="h4">Who we are looking for</Header>
                    <p className="sourcing-explainer">
                        What the national provider registry holds about this
                        person. Every page on the right was checked against
                        these facts.
                    </p>
                    <DetailItem label="Specialty">
                        {(expert.specialties || []).join(', ')}
                    </DetailItem>
                    <DetailItem label="Credential">
                        {(expert.credentials || []).join(', ')}
                    </DetailItem>
                    <DetailItem label="Where">
                        {[expert.city, expert.region]
                            .filter(Boolean)
                            .join(', ')}
                    </DetailItem>
                    <DetailItem label="Employer">
                        {(expert.organizations || []).length > 0
                            ? expert.organizations.map((name) => (
                                  <div key={name}>{name}</div>
                              ))
                            : '—'}
                    </DetailItem>

                    <Header as="h4">What we searched</Header>
                    <p className="sourcing-explainer">
                        Up to three searches per person. The pages on the right
                        are what came back.
                    </p>
                    {(expert.searches || []).map((one) => (
                        <div className="sourcing-search" key={one.kind}>
                            <div className="sourcing-search-head">
                                <strong>
                                    {SEARCH_LABELS[one.kind] || one.kind}
                                </strong>
                                <span className="auth-user-muted">
                                    {one.resultCount} result
                                    {one.resultCount === 1 ? '' : 's'}
                                </span>
                            </div>
                            <div className="sourcing-field-label">Query</div>
                            <code className="sourcing-query">{one.query}</code>
                            {one.unresponsiveEngines.length > 0 && (
                                <>
                                    {/* Worth surfacing rather than hiding: a
                                        thin set of results usually means the
                                        engines refused us, not that the web
                                        has nothing on this person. */}
                                    <div className="sourcing-field-label">
                                        Search engines that did not answer
                                    </div>
                                    <ul className="sourcing-engines">
                                        {one.unresponsiveEngines.map(
                                            (entry) => {
                                                const { engine, reason } =
                                                    splitEngineReason(entry)

                                                return (
                                                    <li key={entry}>
                                                        <strong>
                                                            {engine}
                                                        </strong>
                                                        {reason
                                                            ? ` — ${reason}`
                                                            : ''}
                                                    </li>
                                                )
                                            }
                                        )}
                                    </ul>
                                </>
                            )}
                        </div>
                    ))}
                    {expert.registryDirectoryCount > 0 && (
                        <p className="auth-user-muted sourcing-dead">
                            {expert.registryDirectoryCount} result
                            {expert.registryDirectoryCount === 1
                                ? ' was'
                                : 's were'}{' '}
                            left out because they are sites that republish the
                            national provider registry — they can only repeat
                            what the panel above already says.
                        </p>
                    )}
                </aside>

                <section className="sourcing-pages">
                    {pages.length === 0 && (
                        <Message
                            info
                            content="No pages were found for this person."
                        />
                    )}

                    {possiblyThem.map((page) => (
                        <PageCard key={page.url} page={page} terms={terms} />
                    ))}

                    {otherPeople.length > 0 && (
                        <>
                            {/* Pages about someone else are kept and shown on
                                purpose: the check has been wrong in both
                                directions, and a reviewer cannot tell us we
                                discarded a good page if the page is hidden. */}
                            <button
                                type="button"
                                className="sourcing-toggle"
                                onClick={() =>
                                    setShowOtherPeople(!showOtherPeople)
                                }
                            >
                                <Icon
                                    name={
                                        showOtherPeople
                                            ? 'caret down'
                                            : 'caret right'
                                    }
                                />
                                {showOtherPeople ? 'Hide' : 'Show'}{' '}
                                {otherPeople.length} page
                                {otherPeople.length === 1 ? '' : 's'} judged to
                                be someone else
                            </button>
                            {showOtherPeople &&
                                otherPeople.map((page) => (
                                    <PageCard
                                        key={page.url}
                                        page={page}
                                        terms={terms}
                                    />
                                ))}
                        </>
                    )}
                </section>
            </div>
        </div>
    )
}

export default ExternalExpertDetail
